import { useState, type FormEvent } from "react";
import type { Movie, MovieReview } from "@/mocks/movies";

interface ReviewSectionProps {
  movie: Movie;
}

export default function ReviewSection({ movie }: ReviewSectionProps) {
  const [reviews, setReviews] = useState<MovieReview[]>(movie.reviews);
  const [name, setName] = useState("");
  const [rating, setRating] = useState(8);
  const [content, setContent] = useState("");
  const [submitted, setSubmitted] = useState(false);

  const MAX = 500;

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!content.trim()) return;
    const newReview: MovieReview = {
      id: Date.now(),
      user: name.trim() || "匿名影迷",
      avatar: "",
      rating,
      date: new Date().toISOString().slice(0, 10),
      content: content.trim(),
    };
    setReviews((prev) => [newReview, ...prev]);
    setName("");
    setRating(8);
    setContent("");
    setSubmitted(true);
    window.setTimeout(() => setSubmitted(false), 2500);
  };

  return (
    <section className="mt-10" aria-label="影评精选">
      <div className="flex items-end justify-between gap-4">
        <div>
          <p className="font-label text-sm font-medium text-accent-600">观众声音</p>
          <h2 className="mt-1.5 font-heading text-xl font-bold text-foreground-950 md:text-2xl">影评精选</h2>
        </div>
        <span className="font-label text-xs text-foreground-500">共 {reviews.length} 条</span>
      </div>

      <form onSubmit={handleSubmit} className="mt-5 rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
        <div className="flex flex-wrap items-center gap-3">
          <input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="你的昵称（选填）"
            className="min-w-0 flex-1 rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
          />
          <div className="flex items-center gap-2">
            <span className="font-label text-xs text-foreground-500">评分</span>
            <select
              value={rating}
              onChange={(e) => setRating(Number(e.target.value))}
              className="rounded-md border border-background-300 bg-background-50 px-3 py-2 font-label text-sm text-foreground-900 focus:border-primary-400 focus:outline-none cursor-pointer"
              aria-label="评分"
            >
              {[10, 9, 8, 7, 6, 5, 4, 3, 2, 1].map((n) => (
                <option key={n} value={n}>
                  {n}.0 分
                </option>
              ))}
            </select>
          </div>
        </div>

        <textarea
          value={content}
          onChange={(e) => setContent(e.target.value.slice(0, MAX))}
          rows={3}
          maxLength={MAX}
          placeholder="写下你对这部电影的观后感…"
          className="mt-3 w-full resize-none rounded-md border border-background-300 bg-background-50 px-3 py-2.5 text-sm leading-relaxed text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
        ></textarea>

        <div className="mt-3 flex flex-wrap items-center justify-between gap-3">
          <p className="font-label text-xs text-foreground-400">
            {content.length}/{MAX} 字 · 本评论为前端演示，不会保存数据
          </p>
          <div className="flex items-center gap-3">
            {submitted && (
              <span className="animate-fade-in font-label text-xs text-accent-700">评论已发布（演示）</span>
            )}
            <button
              type="submit"
              disabled={!content.trim()}
              className="inline-flex items-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 font-label text-sm text-background-50 transition-colors hover:bg-primary-600 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
            >
              <i className="ri-send-plane-line" aria-hidden="true"></i>
              发布评论
            </button>
          </div>
        </div>
      </form>

      <div className="mt-6 space-y-4">
        {reviews.length > 0 ? (
          reviews.map((review) => (
            <article key={review.id} className="rounded-lg border border-background-200 bg-background-50 p-4 md:p-5">
              <div className="flex items-center gap-3">
                <span className="flex h-10 w-10 shrink-0 items-center justify-center overflow-hidden rounded-full bg-primary-100 font-heading text-sm font-semibold text-primary-700">
                  {review.avatar ? (
                    <img src={review.avatar} alt={review.user} className="h-full w-full object-cover" />
                  ) : (
                    review.user.slice(0, 1)
                  )}
                </span>
                <div className="min-w-0 flex-1">
                  <p className="truncate font-label text-sm font-medium text-foreground-900">{review.user}</p>
                  <p className="font-label text-xs text-foreground-500">{review.date}</p>
                </div>
                <span className="inline-flex items-center gap-1 rounded-md bg-accent-100 px-2.5 py-1 font-label text-xs font-semibold text-accent-900">
                  <i className="ri-star-fill" aria-hidden="true"></i>
                  {review.rating}.0
                </span>
              </div>
              <p className="mt-3 text-sm leading-relaxed text-foreground-700">{review.content}</p>
            </article>
          ))
        ) : (
          <div className="rounded-lg border border-dashed border-background-300 bg-background-50 px-6 py-10 text-center">
            <p className="text-sm text-foreground-500">暂无精选影评，欢迎写下第一条观后感。</p>
          </div>
        )}
      </div>
    </section>
  );
}